import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LeafUp } from './icons';
import { useTransfers } from '../../hooks/useTransfers';
import { useCardTransactions } from '../../hooks/useCards';
import { useWallet } from '../../hooks/useWallet';
import { useUiStore } from '../../store/ui';

/** Net change in the total balance today. Card balances are part of the total,
 *  so this nets today's *real* money movements of the wallet + cards by counting
 *  every leg by direction:
 *   • external wallet sends/receives (in − out − network fees);
 *   • card spending is a loss, card refunds a gain.
 *  Internal wallet↔card moves have two opposite-sign legs that cancel, leaving
 *  only their commissions as the net loss — e.g. a top-up nets the wallet
 *  `card_payment` out (base + recharge fee + network fee) against the card
 *  `topup` credit (base), so exactly the recharge + network fees remain.
 *  Stablecoins don't appreciate, so there's no price P&L. Always rendered —
 *  neutral $0.00 when nothing moved today. Shared by the home header + wallet tab. */
export function TodayChange({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { transfers } = useTransfers();
  const { txs: cardTxs } = useCardTransactions();
  const { priceBySymbol } = useWallet();
  const balanceHidden = useUiStore((s) => s.balanceHidden);

  const change = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const startMs = start.getTime();

    // On-chain wallet transfers (network fee is a real cost on every send).
    // Native ETH/BTC amounts are COIN units — convert to USD at the live spot
    // (skip the row entirely while the price is unknown rather than counting
    // 0.5 BTC as $0.50).
    const walletNet = transfers.reduce((net, tr) => {
      if (tr.status !== 'success') return net;
      if (new Date(tr.createdAt).getTime() < startMs) return net;
      const isNative = tr.token === 'ETH' || tr.token === 'BTC' || tr.token === 'TRX';
      const price = isNative ? priceBySymbol[tr.token] : 1;
      if (price == null) return net;
      const amt = (Number(tr.amount) || 0) * price;
      return tr.direction === 'in' ? net + amt : net - amt - (Number(tr.feeUsd) || 0);
    }, 0);

    // Card transactions: spend (out) is a loss, refund / top-up credit (in) a
    // gain. The top-up credit offsets the wallet `card_payment` out above.
    const cardNet = cardTxs.reduce((net, tx) => {
      if (tx.status !== 'success') return net;
      if ((tx.sortTs ?? 0) < startMs) return net;
      const amt = Number(tx.tokenAmount) || 0;
      return tx.direction === 'in' ? net + amt : net - amt;
    }, 0);

    return walletNet + cardNet;
  }, [transfers, cardTxs, priceBySymbol]);

  const up = !balanceHidden && change > 0.005;
  const down = !balanceHidden && change < -0.005;

  return (
    <div
      className={`flex items-center gap-[2px] rounded-full p-2 ${
        up ? 'bg-positive/[0.12]' : down ? 'bg-negative/[0.12]' : 'bg-black/[0.05]'
      } ${className ?? ''}`}
    >
      <LeafUp
        size={14}
        className={up ? 'text-positive' : down ? 'rotate-180 text-negative' : 'text-muted'}
      />
      <span
        className={`text-[14px] leading-none ${
          up ? 'text-positive' : down ? 'text-negative' : 'text-muted'
        }`}
      >
        {balanceHidden
          ? `•••• ${t('wallet.today')}`
          : `${up ? '+' : down ? '−' : ''}$${Math.abs(change).toFixed(2)} ${t('wallet.today')}`}
      </span>
    </div>
  );
}
